import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import '../assets/global.css';
import { FiPackage, FiUsers, FiBookOpen, FiDollarSign } from 'react-icons/fi';

const resourcesDropdownStyle = {
  width: '650px',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  padding: '8px',
  border: '1px solid #E5E7EB',
  right: '0',
  left: 'auto'
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featuresDropdownOpen, setFeaturesDropdownOpen] = useState(false);
  const [solutionsDropdownOpen, setSolutionsDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const featuresDropdownRef = useRef(null);
  const solutionsDropdownRef = useRef(null);
  const resourcesDropdownRef = useRef(null);
  const navigate = useNavigate();

  // Add animation controls
  const logoControls = useAnimation();
  const mobileMenuControls = useAnimation();

  useEffect(() => {
    logoControls.start({ opacity: 1, x: 0 });
    
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    const handleClickOutside = (event) => {
      if (featuresDropdownRef.current && !featuresDropdownRef.current.contains(event.target)) {
        setFeaturesDropdownOpen(false);
      }
      if (solutionsDropdownRef.current && !solutionsDropdownRef.current.contains(event.target)) {
        setSolutionsDropdownOpen(false);
      }
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target)) {
        setResourcesDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [logoControls]);

  // Update mobile menu animation when state changes
  useEffect(() => {
    if (mobileMenuOpen) {
      mobileMenuControls.start({ 
        height: 'auto', 
        opacity: 1,
        transition: { duration: 0.3 }
      });
    } else {
      mobileMenuControls.start({ 
        height: 0, 
        opacity: 0,
        transition: { duration: 0.3 }
      });
    }
  }, [mobileMenuOpen, mobileMenuControls]);

  // Add function to ensure dropdowns are contained within viewport
  const ensureDropdownVisibility = (ref) => {
    if (!ref.current) return;
    setTimeout(() => {
      const dropdown = ref.current.querySelector('.resources-dropdown, .features-dropdown');
      if (!dropdown) return;
      const rect = dropdown.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      if (rect.right > viewportWidth) {
        dropdown.style.left = 'auto';
        dropdown.style.right = '0';
      } else {
        dropdown.style.left = '0';
        dropdown.style.right = 'auto';
      }
    }, 0);
  };

  const features = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      title: "Data Sources",
      description: "Connect to all your data platforms seamlessly",
      href: "/features/data-sources"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "Auto Discovery",
      description: "Automatically detect schema, tables, and dashboards",
      href: "/features/auto-discovery"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Profiling",
      description: "Get quick insights on your data's shape and quality",
      href: "/features/profiling"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      ),
      title: "Quality Checks",
      description: "Comprehensive rules for data quality monitoring",
      href: "/features/quality-checks"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Advanced Lineage",
      description: "Trace data through your entire ecosystem",
      href: "/features/lineage"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "AI-Powered Platform",
      description: "Anomaly detection and smart recommendations",
      href: "/features/ai-platform"
    }
  ];

  const solutions = {
    byUseCase: [
      {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        ),
        title: "Data quality monitoring and testing",
        description: "Deploy and manage monitors and tests on one platform.",
        href: "/solutions/by-use-case/data-quality"
      },
      {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        ),
        title: "Report and dashboard integrity",
        description: "Produce reliable data your company can trust.",
        href: "/solutions/by-use-case/dashboard-integrity"
      },
      {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        title: "Data mesh and self serve data",
        description: "Empower data producers and consumers to self-serve.",
        href: "/solutions/by-use-case/data-mesh"
      }
    ],
    byPersona: [
      {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
        title: "Data Leader",
        description: "Drive strategic value from your data assets.",
        href: "/solutions/by-persona/data-leader"
      },
      {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        ),
        title: "Data Engineer",
        description: "Build reliable data pipelines with automated testing.",
        href: "/solutions/by-persona/data-engineer"
      },
      {
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        ),
        title: "Data User",
        description: "Access trustworthy data with confidence.",
        href: "/solutions/by-persona/data-user"
      }
    ]
  };

  const resources = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
      title: "Blog",
      description: "From data teams to data teams",
      href: "https://learn.qupid.io/blog"
    },
    // {
    //   icon: (
    //     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    //     </svg>
    //   ),
    //   title: "Qupid Foundations",
    //   description: "Video series",
    //   href: "https://learn.qupid.io/tutorial/intro/"
    // },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: "Docs",
      description: "Resources and product guides",
      href: "https://learn.qupid.io/docs"
    },
    // {
    //   icon: (
    //     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
    //     </svg>
    //   ),
    //   title: "Changelog",
    //   description: "Product updates",
    //   href: "https://learn.qupid.io/changelog/"
    // },
    // {
    //   icon: (
    //     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    //     </svg>
    //   ),
    //   title: "Resources & Events",
    //   description: "Learn and connect with peers",
    //   href: "https://learn.qupid.io/"
    // },
    // {
    //   icon: (
    //     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    //     </svg>
    //   ),
    //   title: "Customer Stories",
    //   description: "Empowering the best data teams",
    //   href: "https://learn.qupid.io/"
    // },
    // {
    //   icon: (
    //     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    //     </svg>
    //   ),
    //   title: "Partners",
    //   description: "Learn more about our partnerships",
    //   href: "https://learn.qupid.io/"
    // }
  ];

  // Common style for dropdown containers
  const dropdownContainerStyle = {
    maxHeight: '80vh',
    overflowY: 'auto',
    position: 'absolute',
    zIndex: 1000
  };

  const handleRequestDemo = (e) => {
    e.preventDefault();
    if (window.location.pathname === '/') {
      // If we're on the home page, scroll to the section
      const element = document.getElementById('request-demo');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on another page, navigate to home page with hash
      navigate('/#request-demo');
    }
  };

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center relative">
        {/* Logo */}
        <div className="flex items-center flex-shrink-0">
          <Link to="/" className="flex items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={logoControls}
              transition={{ duration: 0.5 }}
            >
              <img 
                src="/images/qupid.svg" 
                alt="Qupid Logo" 
                className="h-8" 
              />
            </motion.div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {/* Product Dropdown */}
          <div 
            className="relative dropdown-container" 
            ref={featuresDropdownRef}
            onMouseEnter={() => {
              setFeaturesDropdownOpen(true);
              setSolutionsDropdownOpen(false);
              setResourcesDropdownOpen(false);
            }}
            onMouseLeave={() => setFeaturesDropdownOpen(false)}
          >
            <button
              className="text-base font-medium text-gray-700 hover:text-primary transition-colors flex items-center gap-1"
            >
              <FiPackage className="inline-block mr-1" />
              Product
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${featuresDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            
            {/* Features dropdown */}
            {featuresDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -5, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute left-0 mt-0 features-dropdown z-50"
                style={{ 
                  ...dropdownContainerStyle, 
                  maxWidth: 'calc(100vw - 40px)',
                  top: '100%',
                  marginTop: '0'
                }}
                onMouseEnter={() => ensureDropdownVisibility(featuresDropdownRef)}
              >
                <div className="h-2 absolute w-full -top-2" style={{ background: 'transparent' }}></div>
                {features.map((feature, index) => (
                  <Link
                    key={index}
                    to={feature.href}
                    className="dropdown-item"
                    onClick={() => setFeaturesDropdownOpen(false)}
                  >
                    <div className="icon-container text-primary">
                      {feature.icon}
                    </div>
                    <div className="feature-content">
                      <div className="feature-title">{feature.title}</div>
                      <div className="feature-description">{feature.description}</div>
                    </div>
                  </Link>
                ))}
              </motion.div>
            )}
          </div>
          
          {/* Solutions Dropdown */}
          <div 
            className="relative dropdown-container" 
            ref={solutionsDropdownRef}
            onMouseEnter={() => {
              setSolutionsDropdownOpen(true);
              setFeaturesDropdownOpen(false);
              setResourcesDropdownOpen(false);
            }}
            onMouseLeave={() => setSolutionsDropdownOpen(false)}
          >
            <button
              className="text-base font-medium text-gray-700 hover:text-primary transition-colors flex items-center gap-1"
            >
              <FiUsers className="inline-block mr-1" />
              Solutions
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${solutionsDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            
            {/* Solutions dropdown */}
            {solutionsDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -5, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute left-0 mt-0 features-dropdown z-50"
                style={{ 
                  ...dropdownContainerStyle, 
                  maxWidth: 'calc(100vw - 40px)',
                  top: '100%',
                  marginTop: '0'
                }}
                onMouseEnter={() => ensureDropdownVisibility(solutionsDropdownRef)}
              >
                <div className="dropdown-section">
                  <div className="dropdown-section-header px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">By Use Case</div>
                  {solutions.byUseCase.map((solution, index) => (
                    <Link
                      key={`use-case-${index}`}
                      to={solution.href}
                      className="dropdown-item"
                      onClick={() => setSolutionsDropdownOpen(false)}
                    >
                      <div className="icon-container text-primary">
                        {solution.icon}
                      </div>
                      <div className="feature-content">
                        <div className="feature-title">{solution.title}</div>
                        <div className="feature-description">{solution.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
                
                <div className="dropdown-section">
                  <div className="dropdown-section-header px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">By Persona</div>
                  {solutions.byPersona.map((solution, index) => (
                    <Link
                      key={`persona-${index}`}
                      to={solution.href}
                      className="dropdown-item"
                      onClick={() => setSolutionsDropdownOpen(false)}
                    >
                      <div className="icon-container text-primary">
                        {solution.icon}
                      </div>
                      <div className="feature-content">
                        <div className="feature-title">{solution.title}</div>
                        <div className="feature-description">{solution.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
          
          {/* Resources Dropdown */}
          <div 
            className="relative dropdown-container" 
            ref={resourcesDropdownRef}
            onMouseEnter={() => {
              setResourcesDropdownOpen(true);
              setFeaturesDropdownOpen(false);
              setSolutionsDropdownOpen(false);
            }}
            onMouseLeave={() => setResourcesDropdownOpen(false)}
          >
            <button
              className="text-base font-medium text-gray-700 hover:text-primary transition-colors flex items-center gap-1"
            >
              <FiBookOpen className="inline-block mr-1" />
              Resources
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${resourcesDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            
            {/* Resources dropdown */}
            {resourcesDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -5, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute mt-0 resources-dropdown z-50"
                style={{ ...resourcesDropdownStyle, ...dropdownContainerStyle, maxWidth: 'calc(100vw - 40px)' }}
              >
                {resources.map((resource, index) => (
                  <Link
                    key={index}
                    to={resource.href}
                    className="dropdown-item"
                    onClick={() => setResourcesDropdownOpen(false)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="icon-container text-primary">
                      {resource.icon}
                    </div>
                    <div className="feature-content">
                      <div className="feature-title">{resource.title}</div>
                      <div className="feature-description">{resource.description}</div>
                    </div>
                  </Link>
                ))}
              </motion.div>
            )}
          </div>
          
          {/* Pricing Link */}
          <NavLink href="/pricing">
            <FiDollarSign className="inline-block mr-1" />
            Pricing
          </NavLink>
          
          <button onClick={handleRequestDemo} className="button button-primary ml-4">
            Request Demo
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex-shrink-0 md:hidden">
          <button
            className="flex items-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {mobileMenuOpen ? (
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M4 6H20M4 12H20M4 18H20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <motion.div
        className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-md overflow-y-auto transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-[90vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
        initial={{ height: 0, opacity: 0 }}
        animate={mobileMenuControls}
        style={{ 
          WebkitOverflowScrolling: 'touch',
          maxHeight: mobileMenuOpen ? '80vh' : '0',
          overflowY: 'auto'
        }}
      >
        <div className="container mx-auto py-5 flex flex-col space-y-3 max-h-[80vh] overflow-y-auto">
          {/* Mobile Features Submenu */}
          <div className="border-b border-gray-200 pb-3">
            <p className="text-lg font-semibold text-primary mb-2">
              <FiPackage className="inline-block mr-1" />
              Features
            </p>
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.href}
                className="flex items-center py-2 pl-4 text-base font-medium text-gray-700 hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="flex-shrink-0 h-5 w-5 text-primary">
                  {feature.icon}
                </div>
                <span className="ml-2">{feature.title}</span>
              </Link>
            ))}
          </div>
          
          {/* Mobile Solutions Submenu */}
          <div className="border-b border-gray-200 pb-3">
            <p className="text-lg font-semibold text-primary mb-2">
              <FiUsers className="inline-block mr-1" />
              Solutions
            </p>
            
            <div className="mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase px-4 py-1.5 bg-gray-50 rounded-t-md">By Use Case</p>
              {solutions.byUseCase.map((solution, index) => (
                <Link
                  key={`use-case-${index}`}
                  to={solution.href}
                  className="flex items-center py-2 pl-4 text-base font-medium text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="flex-shrink-0 h-5 w-5 text-primary">
                    {solution.icon}
                  </div>
                  <span className="ml-2">{solution.title}</span>
                </Link>
              ))}
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase px-4 py-1.5 bg-gray-50 rounded-t-md">By Persona</p>
              {solutions.byPersona.map((solution, index) => (
                <Link
                  key={`persona-${index}`}
                  to={solution.href}
                  className="flex items-center py-2 pl-4 text-base font-medium text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="flex-shrink-0 h-5 w-5 text-primary">
                    {solution.icon}
                  </div>
                  <span className="ml-2">{solution.title}</span>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Mobile Resources Submenu */}
          <div className="border-b border-gray-200 pb-3">
            <p className="text-lg font-semibold text-primary mb-2">
              <FiBookOpen className="inline-block mr-1" />
              Resources
            </p>
            {resources.map((resource, index) => (
              <Link
                key={index}
                to={resource.href}
                className="flex items-center py-2 pl-4 text-base font-medium text-gray-700 hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="flex-shrink-0 h-5 w-5 text-primary">
                  {resource.icon}
                </div>
                <span className="ml-2">{resource.title}</span>
              </Link>
            ))}
          </div>
          
          {/* Mobile Menu Items */}
          <div className={`lg:hidden py-6 px-5 space-y-3 ${mobileMenuOpen ? 'block' : 'hidden'}`}>
            <MobileNavLink href="/pricing" onClick={() => setMobileMenuOpen(false)}>
              Pricing
            </MobileNavLink>
          </div>
          
          <button onClick={handleRequestDemo} className="button button-primary w-full text-center mt-3">
            Request Demo
          </button>
        </div>
      </motion.div>
    </header>
  );
};

const NavLink = ({ href, children }) => {
  return (
    <Link
      to={href}
      className="text-base font-medium text-gray-700 hover:text-primary transition-colors"
    >
      {children}
    </Link>
  );
};

const MobileNavLink = ({ href, onClick, children }) => {
  return (
    <Link
      to={href}
      onClick={onClick}
      className="block py-2 text-base font-medium text-gray-700 hover:text-primary transition-colors"
    >
      {children}
    </Link>
  );
};

export default Header; 